// Drag & Sort — React port of kula-create's drag-sort.html using @dnd-kit/core.
//
// Pedagogy: classification or sequencing. Items start in a shared pool;
// learner drags each into a zone (categorise mode) or orders them in a
// sequence zone (sequence mode, with up/down reordering within the zone).
// Submit checks against the correct zone + correctPosition.
//
// Single-row capture on submit:
//   { placements: Array<{ itemId, zoneId, position? }> }
// where `position` is the 0-based index within the zone (sequence mode only).

import { useState, useCallback, useMemo, useRef } from 'react';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
  type DragEndEvent,
} from '@dnd-kit/core';
import { AnimatePresence } from 'framer-motion';
import type { DragSortSlideData, Theme } from '../../slideTypes';
import { slideTypographyStyle } from '../../slideTypes';
import { feedbackStyle } from '../../shared/SlideShared';
import { Reveal } from '../../shared/Reveal';
import { ExperienceClose } from '../../shared/ExperienceClose';
import type { ActivityResponsePayload } from '../../../../../types';
import { HyperpopDragSort } from './HyperpopDragSort';

interface Props {
  data: DragSortSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: DragSortSlideData) => void;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}

export function DragSortSlide({ data, theme, isEditing, onUpdate, onActivityResponse, onComplete, savedResponse }: Props) {
  if (isEditing) return <BasicEditor data={data} theme={theme} onUpdate={onUpdate} />;
  // Tier-A bespoke renderer for the "Hyperpop Arcade" world (home-set pairing #2).
  // It accepts onComplete + savedResponse and fires onComplete once on submit
  // ("lock in my sort").
  if (theme.id === 'hyperpop') {
    return (
      <HyperpopDragSort
        data={data}
        theme={theme}
        onActivityResponse={onActivityResponse}
        onComplete={onComplete}
        savedResponse={savedResponse}
      />
    );
  }
  return (
    <Student
      data={data}
      theme={theme}
      onActivityResponse={onActivityResponse}
      onComplete={onComplete}
      savedResponse={savedResponse}
    />
  );
}

const POOL_ZONE = '__pool__';

interface PlacementEntry {
  itemId: string;
  zoneId: string;
  position?: number;
}

function Student({
  data,
  theme,
  onActivityResponse,
  onComplete,
  savedResponse,
}: {
  data: DragSortSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}) {
  const accent = theme.colors.accent || theme.colors.primary;
  const feedbackCorrect = feedbackStyle(theme, 'correct');

  // A saved payload's placements fully reconstruct the submitted state
  // (positions included), so restore straight into the result view.
  const savedPlacements = useMemo(() => {
    const raw = savedResponse?.placements;
    if (!Array.isArray(raw)) return null;
    return raw.filter(
      (p): p is PlacementEntry =>
        !!p &&
        typeof p === 'object' &&
        typeof (p as PlacementEntry).itemId === 'string' &&
        typeof (p as PlacementEntry).zoneId === 'string',
    );
  }, [savedResponse]);

  // zoneId → ordered itemIds; items in no zone are still in the pool.
  const [zoneOrder, setZoneOrder] = useState<Record<string, string[]>>(() => {
    const next: Record<string, string[]> = {};
    for (const zone of data.zones) next[zone.id] = [];
    if (savedPlacements) {
      const sorted = [...savedPlacements].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
      for (const p of sorted) {
        if (next[p.zoneId] && data.items.some((i) => i.id === p.itemId)) next[p.zoneId].push(p.itemId);
      }
    }
    return next;
  });
  const [submitted, setSubmitted] = useState(!!savedPlacements);
  const completedRef = useRef(!!savedPlacements);

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (submitted) return;
      const itemId = String(event.active.id);
      const zoneId = event.over ? String(event.over.id) : POOL_ZONE;
      setZoneOrder((prev) => {
        const next: Record<string, string[]> = {};
        for (const [zid, ids] of Object.entries(prev)) next[zid] = ids.filter((id) => id !== itemId);
        if (zoneId !== POOL_ZONE && next[zoneId]) next[zoneId] = [...next[zoneId], itemId];
        return next;
      });
    },
    [submitted],
  );

  // Sequence mode: reorder an item within its zone.
  const moveItem = useCallback((zoneId: string, index: number, delta: number) => {
    setZoneOrder((prev) => {
      const ids = [...(prev[zoneId] ?? [])];
      const target = index + delta;
      if (target < 0 || target >= ids.length) return prev;
      [ids[index], ids[target]] = [ids[target], ids[index]];
      return { ...prev, [zoneId]: ids };
    });
  }, []);

  const itemsByZone = useMemo(() => {
    const byZone: Record<string, DragSortSlideData['items']> = {};
    const placed = new Set<string>();
    for (const zone of data.zones) {
      byZone[zone.id] = (zoneOrder[zone.id] ?? [])
        .map((id) => data.items.find((i) => i.id === id))
        .filter((i): i is DragSortSlideData['items'][number] => !!i);
      for (const item of byZone[zone.id]) placed.add(item.id);
    }
    const pool = data.items.filter((i) => !placed.has(i.id));
    return { byZone, pool };
  }, [data.items, data.zones, zoneOrder]);

  const allPlaced = itemsByZone.pool.length === 0;
  const hasItems = data.items.length > 0;

  // Correct = right zone, plus right position when sequence mode authors one.
  const isItemCorrect = useCallback(
    (item: DragSortSlideData['items'][number], zoneId: string, position: number) =>
      item.correctZone === zoneId &&
      (data.mode !== 'sequence' || item.correctPosition === undefined || item.correctPosition === position),
    [data.mode],
  );

  // Derived after submit so "Try again" only needs to flip `submitted`.
  const feedback = useMemo(() => {
    if (!submitted || !hasItems) return null;
    let correct = 0;
    for (const zone of data.zones) {
      itemsByZone.byZone[zone.id].forEach((item, position) => {
        if (isItemCorrect(item, zone.id, position)) correct += 1;
      });
    }
    return { correct, total: data.items.length };
  }, [submitted, hasItems, data.zones, data.items.length, itemsByZone, isItemCorrect]);

  const submit = useCallback(() => {
    if (!hasItems) return;
    const payload: PlacementEntry[] = data.items.map((item) => {
      const zoneId = data.zones.find((z) => (zoneOrder[z.id] ?? []).includes(item.id))?.id ?? POOL_ZONE;
      const entry: PlacementEntry = { itemId: item.id, zoneId };
      if (data.mode === 'sequence' && zoneId !== POOL_ZONE) {
        entry.position = (zoneOrder[zoneId] ?? []).indexOf(item.id);
      }
      return entry;
    });
    const correct = payload.filter((p) => {
      const item = data.items.find((i) => i.id === p.itemId);
      return !!item && isItemCorrect(item, p.zoneId, p.position ?? -1);
    }).length;
    setSubmitted(true);
    onActivityResponse?.({
      activityType: 'drag-sort',
      mode: 'quiz',
      responseData: { placements: payload },
      isCorrect: correct === data.items.length,
      score: Math.round((correct / data.items.length) * 100),
    });
    if (!completedRef.current) {
      completedRef.current = true;
      onComplete?.();
    }
  }, [hasItems, data.items, data.zones, data.mode, zoneOrder, isItemCorrect, onActivityResponse, onComplete]);

  const tryAgain = useCallback(() => setSubmitted(false), []);

  return (
    <div
      className="h-full w-full overflow-y-auto"
      style={{ background: theme.colors.background, color: theme.colors.text, fontFamily: theme.fonts.body }}
    >
      <div className="mx-auto max-w-[820px] px-6 py-10">
        <header className="text-center mb-6">
          <h1 style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text, marginBottom: 8 }}>
            {data.title}
          </h1>
          {data.subtitle && <p style={{ color: theme.colors.textMuted, fontSize: '0.95rem' }}>{data.subtitle}</p>}
          {data.instruction && (
            <p className="text-sm mt-3 max-w-xl mx-auto" style={{ color: theme.colors.text }}>
              {data.instruction}
            </p>
          )}
        </header>

        {!hasItems ? (
          <div
            className="rounded-lg p-6 border-2 text-center"
            style={{ background: theme.colors.surface, borderColor: `${accent}30`, borderStyle: 'dashed' }}
          >
            <p className="text-sm italic" style={{ color: theme.colors.textMuted }}>
              No items to sort yet — this activity has no content.
            </p>
          </div>
        ) : (
          <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
            {/* Pool */}
            <DropZone id={POOL_ZONE} label="Items" theme={theme} accent={accent}>
              {itemsByZone.pool.map((item) => (
                <DraggableItem key={item.id} item={item} theme={theme} accent={accent} disabled={submitted} />
              ))}
              {itemsByZone.pool.length === 0 && (
                <p className="text-xs italic" style={{ color: theme.colors.textMuted }}>
                  All items placed.
                </p>
              )}
            </DropZone>

            {/* Zones */}
            <div className={data.mode === 'sequence' ? 'space-y-3 mt-4' : 'grid sm:grid-cols-2 gap-3 mt-4'}>
              {data.zones.map((zone) => (
                <DropZone
                  key={zone.id}
                  id={zone.id}
                  label={zone.label}
                  theme={theme}
                  accent={accent}
                  stacked={data.mode === 'sequence'}
                >
                  {itemsByZone.byZone[zone.id].map((item, idx) => {
                    const isRight = submitted && isItemCorrect(item, zone.id, idx);
                    const isWrong = submitted && !isRight;
                    return (
                      <div key={item.id} className="flex items-center gap-1.5">
                        <DraggableItem
                          item={item}
                          theme={theme}
                          accent={accent}
                          disabled={submitted}
                          state={isRight ? 'correct' : isWrong ? 'incorrect' : undefined}
                          note={isWrong ? data.feedback.perItem?.[item.id] : undefined}
                        />
                        {data.mode === 'sequence' && !submitted && (
                          <span className="flex flex-col">
                            <button
                              onClick={() => moveItem(zone.id, idx, -1)}
                              disabled={idx === 0}
                              aria-label={`Move ${item.text} up`}
                              className="text-xs leading-none px-1"
                              style={{ color: idx === 0 ? `${theme.colors.textMuted}55` : theme.colors.textMuted }}
                            >
                              ▲
                            </button>
                            <button
                              onClick={() => moveItem(zone.id, idx, 1)}
                              disabled={idx === itemsByZone.byZone[zone.id].length - 1}
                              aria-label={`Move ${item.text} down`}
                              className="text-xs leading-none px-1"
                              style={{
                                color:
                                  idx === itemsByZone.byZone[zone.id].length - 1
                                    ? `${theme.colors.textMuted}55`
                                    : theme.colors.textMuted,
                              }}
                            >
                              ▼
                            </button>
                          </span>
                        )}
                      </div>
                    );
                  })}
                  {itemsByZone.byZone[zone.id].length === 0 && (
                    <p className="text-xs italic" style={{ color: theme.colors.textMuted }}>
                      Drop here.
                    </p>
                  )}
                </DropZone>
              ))}
            </div>
          </DndContext>
        )}

        {hasItems && !submitted && (
          <button
            onClick={submit}
            disabled={!allPlaced}
            className="w-full mt-6 px-6 py-3 rounded-lg font-semibold text-sm"
            style={{
              background: accent,
              color: '#fff',
              cursor: allPlaced ? 'pointer' : 'not-allowed',
              opacity: allPlaced ? 1 : 0.4,
            }}
          >
            Check my answer
          </button>
        )}

        {/* Post-submit summary — the payoff. Rises in with a bumped radius +
            shadow so the verdict reads as a distinct register. */}
        <AnimatePresence>
          {feedback && (
            <Reveal
              variant="up"
              className="mt-6 rounded-xl p-5 text-center border-2 shadow-sm"
              style={{
                background: feedback.correct === feedback.total ? feedbackCorrect.bg : `${accent}15`,
                borderColor: feedback.correct === feedback.total ? feedbackCorrect.border : accent,
              }}
            >
              <p
                className="font-semibold text-sm"
                style={{ color: feedback.correct === feedback.total ? feedbackCorrect.text : accent }}
              >
                {feedback.correct} of {feedback.total} correct
              </p>
              <p className="text-sm mt-2" style={{ color: theme.colors.text }}>
                {feedback.correct === feedback.total ? data.feedback.allCorrect : data.feedback.partial}
              </p>
              {feedback.correct < feedback.total && (
                <button
                  onClick={tryAgain}
                  className="mt-4 px-6 py-2.5 rounded-lg font-semibold text-sm border-2"
                  style={{ background: theme.colors.surface, color: accent, borderColor: accent }}
                >
                  Try again
                </button>
              )}
              {/* Authored "So what?" close — the concluding beat the review asked
                  for (don't end on "re-read"). Only shown when authored: the
                  score line above already serves as the un-authored close, so a
                  bare reflect-back here would just be redundant. */}
              {data.conclusion && (
                <div className="mt-4 text-left">
                  <ExperienceClose
                    conclusion={data.conclusion}
                    theme={theme}
                    values={{ correct: feedback.correct, total: feedback.total }}
                    fallback="Look back at the items you misplaced above."
                  />
                </div>
              )}
            </Reveal>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function DropZone({
  id,
  label,
  theme,
  accent,
  stacked,
  children,
}: {
  id: string;
  label: string;
  theme: Theme;
  accent: string;
  stacked?: boolean;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className="rounded-lg p-4 border-2"
      style={{
        background: theme.colors.surface,
        borderColor: isOver ? accent : `${accent}30`,
        borderStyle: 'dashed',
        minHeight: 80,
      }}
    >
      <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: accent }}>
        {label}
      </p>
      <div className={stacked ? 'flex flex-col gap-2 items-start' : 'flex flex-wrap gap-2'}>{children}</div>
    </div>
  );
}

function DraggableItem({
  item,
  theme,
  accent,
  disabled,
  state,
  note,
}: {
  item: DragSortSlideData['items'][number];
  theme: Theme;
  accent: string;
  disabled?: boolean;
  state?: 'correct' | 'incorrect';
  note?: string;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: item.id,
    disabled,
  });
  const correctFb = feedbackStyle(theme, 'correct');
  const incorrectFb = feedbackStyle(theme, 'incorrect');
  const style: React.CSSProperties = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    background: state === 'correct' ? correctFb.bg : state === 'incorrect' ? incorrectFb.bg : theme.colors.background,
    borderColor: state === 'correct' ? correctFb.border : state === 'incorrect' ? incorrectFb.border : accent,
    color: theme.colors.text,
    opacity: isDragging ? 0.4 : 1,
    cursor: disabled ? 'default' : 'grab',
  };
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className="px-3 py-2 rounded border-2 text-sm font-medium"
      style={style}
    >
      {item.text}
      {note && (
        <span className="block text-xs font-normal mt-1" style={{ color: incorrectFb.text }}>
          {note}
        </span>
      )}
    </div>
  );
}

// ---- Editor (minimal) -----------------------------------------------------

function BasicEditor({
  data,
  theme,
  onUpdate,
}: {
  data: DragSortSlideData;
  theme: Theme;
  onUpdate: (data: DragSortSlideData) => void;
}) {
  return (
    <div className="h-full w-full overflow-y-auto p-8">
      <div className="mx-auto max-w-[720px] space-y-3">
        <input
          value={data.title}
          onChange={(e) => onUpdate({ ...data, title: e.target.value })}
          placeholder="Title…"
          className="w-full bg-transparent border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none"
          style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }}
        />
        <input
          value={data.subtitle}
          onChange={(e) => onUpdate({ ...data, subtitle: e.target.value })}
          placeholder="Subtitle"
          className="w-full bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none text-sm"
          style={{ color: theme.colors.textMuted }}
        />
        <textarea
          value={data.instruction}
          onChange={(e) => onUpdate({ ...data, instruction: e.target.value })}
          placeholder="Instruction"
          rows={2}
          className="w-full px-3 py-2 rounded border text-sm"
          style={{
            background: theme.colors.surface,
            borderColor: `${theme.colors.textMuted}33`,
            color: theme.colors.text,
          }}
        />
        <div className="rounded p-3 text-xs" style={{ background: `${theme.colors.textMuted}11`, color: theme.colors.textMuted }}>
          Mode: <strong>{data.mode}</strong> · {data.items.length} items · {data.zones.length} zone(s).
          Structural edits AI-only — regenerate via AI build.
        </div>
      </div>
    </div>
  );
}
